import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LogOut, Plus, Minus, Flag, Play, Users, RefreshCw, Trash2 } from 'lucide-react';
import GlowingCard from '../components/ui/GlowingCard';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3062/api';

export default function Admin({ onLogout }) {
  const [password, setPassword] = useState('');
  const [loggedIn, setLoggedIn] = useState(false);
  const [courts, setCourts] = useState({ A: null, B: null });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // New states for participants management
  const [showParticipants, setShowParticipants] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [stats, setStats] = useState({ total: 0, waiting: 0, playing: 0, finished: 0 });
  const [selectedForDeletion, setSelectedForDeletion] = useState([]);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setLoggedIn(true);
        fetchCourtStatus();
      } else {
        setError('Invalid password');
      }
    } catch (err) {
      setError('Login failed');
    }
  };

  const fetchCourtStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/admin/courts`);
      const data = await response.json();
      if (data.success) {
        setCourts({
          A: data.courtA,
          B: data.courtB
        });
      }
    } catch (err) {
      console.error('Failed to fetch court status');
    }
  };

  const fetchParticipants = async () => {
    try {
      const response = await fetch(`${API_URL}/admin/participants`, {
        headers: { 'Authorization': 'Bearer admin-session' }
      });
      const data = await response.json();
      if (data.success) {
        setParticipants(data.participants);
        setStats(data.stats);
      }
    } catch (err) {
      console.error('Failed to fetch participants:', err);
    }
  };

  const handleStartMatch = async (court) => {
    const tokenNumber = prompt('Enter token number for Player:');
    if (!tokenNumber) return;

    try {
      const response = await fetch(`${API_URL}/admin/match/start`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': 'Bearer admin-session'
        },
        body: JSON.stringify({ tokenNumber: parseInt(tokenNumber), court })
      });
      
      const data = await response.json();
      if (data.success) {
        if (data.waitingForSecond) {
          alert(`First player added to Court ${court}. Add second player to start match.`);
        }
        fetchCourtStatus();
      } else {
        alert(data.error || 'Failed to start match');
      }
    } catch (err) {
      alert('Error starting match');
    }
  };

  const handleUpdateScore = async (court, playerId, points) => {
    const courtData = courts[court];
    if (!courtData || !playerId) return;

    try {
      const response = await fetch(`${API_URL}/admin/match/score`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': 'Bearer admin-session'
        },
        body: JSON.stringify({ 
          playerId,
          court, 
          points 
        })
      });
      
      if (response.ok) {
        fetchCourtStatus();
      }
    } catch (err) {
      alert('Error updating score');
    }
  };

  const handleFinishMatch = async (court) => {
    const courtData = courts[court];
    if (!courtData || courtData.length < 2) {
      if (!confirm('Finish match with only 1 player?')) return;
    } else if (!confirm('Finish this match and save scores?')) return;

    try {
      const response = await fetch(`${API_URL}/admin/match/finish`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': 'Bearer admin-session'
        },
        body: JSON.stringify({ court })
      });
      
      if (response.ok) {
        fetchCourtStatus();
      }
    } catch (err) {
      alert('Error finishing match');
    }
  };

  const handleDeleteParticipant = async (id, tokenNumber) => {
    if (!confirm(`Delete participant with token #${tokenNumber}? This cannot be undone.`)) return;

    try {
      const response = await fetch(`${API_URL}/admin/participants/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': 'Bearer admin-session' }
      });
      
      const data = await response.json();
      if (data.success) {
        fetchParticipants();
        alert('Participant deleted successfully');
      } else {
        alert(data.error || 'Failed to delete');
      }
    } catch (err) {
      alert('Error deleting participant');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedForDeletion.length === 0) {
      alert('No participants selected');
      return;
    }

    if (!confirm(`Delete ${selectedForDeletion.length} selected participants? This cannot be undone.`)) return;

    try {
      const response = await fetch(`${API_URL}/admin/participants/bulk-delete`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': 'Bearer admin-session'
        },
        body: JSON.stringify({ tokenNumbers: selectedForDeletion })
      });
      
      const data = await response.json();
      if (data.success) {
        fetchParticipants();
        setSelectedForDeletion([]);
        alert(`${data.deletedCount} participants deleted successfully`);
      } else {
        alert(data.error || 'Failed to delete');
      }
    } catch (err) {
      alert('Error deleting participants');
    }
  };

  const toggleSelection = (tokenNumber) => {
    setSelectedForDeletion(prev => 
      prev.includes(tokenNumber)
        ? prev.filter(t => t !== tokenNumber)
        : [...prev, tokenNumber]
    );
  };

  const selectAllFinished = () => {
    const finishedTokens = participants
      .filter(p => p.status === 'finished')
      .map(p => p.tokenNumber);
    setSelectedForDeletion(finishedTokens);
  };

  const clearSelection = () => {
    setSelectedForDeletion([]);
  };

  useEffect(() => {
    if (loggedIn) {
      fetchCourtStatus();
      const interval = setInterval(fetchCourtStatus, 3000);
      return () => clearInterval(interval);
    }
  }, [loggedIn]);

  if (!loggedIn) {
    return (
      <div className="min-h-screen bg-robo-dark flex items-center justify-center p-4">
        <GlowingCard className="p-8 max-w-md w-full">
          <h2 className="text-2xl font-bold text-center mb-6">🔐 Admin Login</h2>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm mb-2">Password</label>
              <input
                type="password"
                className="token-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter admin password"
              />
            </div>
            
            {error && (
              <p className="text-red-400 text-sm">{error}</p>
            )}
            
            <button type="submit" className="neon-button w-full">
              Login
            </button>
          </form>
          
          <button 
            onClick={onLogout}
            className="w-full mt-4 text-center text-sm text-gray-400 hover:text-white"
          >
            ← Back to Home
          </button>
        </GlowingCard>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-robo-dark text-white p-4 md:p-8">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-robo-green flex items-center gap-2">
            🎮 Admin Dashboard
          </h1>
          <p className="text-sm text-gray-400 mt-1">2 Players per Court</p>
        </div>
        <button
          onClick={() => {
            onLogout();
            setLoggedIn(false);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 rounded-xl transition-colors"
        >
          <LogOut className="w-4 h-4" />
          Logout
        </button>
      </div>

      {/* Courts */}
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-6">
        {['A', 'B'].map((court) => {
          const players = courts[court] || [];
          const playerCount = players.length;
          const isMatchReady = playerCount === 2;

          return (
            <GlowingCard key={court} className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <h2 className="text-2xl font-bold">Court {court}</h2>
                  {playerCount > 0 && (
                    <span className={`px-3 py-1 rounded-full text-sm ${
                      isMatchReady 
                        ? 'bg-robo-green/20 text-robo-green' 
                        : 'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      {isMatchReady ? '● Match Live' : `● ${playerCount}/2 Players`}
                    </span>
                  )}
                </div>
              </div>

              {players.length > 0 ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    {players.map((player, index) => (
                      <motion.div 
                        key={player.id}
                        className="bg-white/5 rounded-xl p-4 text-center"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                      >
                        <div className="text-xs text-gray-400 mb-1">
                          Player {index + 1}
                        </div>
                        <p className="text-2xl font-black text-robo-green mb-1">
                          #{player.tokenNumber}
                        </p>
                        <p className="font-semibold text-sm">{player.name}</p>
                        <p className="text-xs text-gray-400">Class {player.className}</p>
                      </motion.div>
                    ))}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {players.map((player) => (
                      <div key={player.id} className="bg-robo-blue/30 rounded-xl p-4">
                        <div className="text-center mb-3">
                          <p className="text-xs text-gray-400 mb-1">Score</p>
                          <p className="text-4xl font-black">{player.score}</p>
                        </div>
                        
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleUpdateScore(court, player.id, -1)}
                            className="w-10 h-10 rounded-lg bg-red-500/20 hover:bg-red-500/30 flex items-center justify-center transition-colors"
                          >
                            <Minus className="w-5 h-5 text-red-400" />
                          </button>
                          
                          <button
                            onClick={() => handleUpdateScore(court, player.id, 1)}
                            className="w-10 h-10 rounded-lg bg-robo-green/20 hover:bg-robo-green/30 flex items-center justify-center transition-colors"
                          >
                            <Plus className="w-5 h-5 text-robo-green" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={() => handleFinishMatch(court)}
                    className={`w-full py-3 rounded-xl flex items-center justify-center gap-2 transition-colors ${
                      isMatchReady
                        ? 'bg-gradient-to-r from-red-500/20 to-orange-500/20 hover:from-red-500/30 hover:to-orange-500/30'
                        : 'bg-yellow-500/20 hover:bg-yellow-500/30'
                    }`}
                  >
                    <Flag className="w-5 h-5" />
                    {isMatchReady ? 'Finish Match' : 'Finish (1 Player)'}
                  </button>

                  {!isMatchReady && (
                    <button
                      onClick={() => handleStartMatch(court)}
                      className="w-full py-3 bg-robo-green/20 hover:bg-robo-green/30 rounded-xl flex items-center justify-center gap-2 transition-colors"
                    >
                      <Users className="w-5 h-5" />
                      Add Player {playerCount + 1}
                    </button>
                  )}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Users className="w-16 h-16 mx-auto mb-4 text-gray-600" />
                  <p className="text-gray-400 mb-4">No active match</p>
                  <button
                    onClick={() => handleStartMatch(court)}
                    className="neon-button flex items-center gap-2 mx-auto"
                  >
                    <Play className="w-5 h-5" />
                    Start Match (Add Player 1)
                  </button>
                </div>
              )}
            </GlowingCard>
          );
        })}
      </div>

      {/* Participants Management Section */}
      <div className="max-w-7xl mx-auto mt-8">
        <GlowingCard>
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Users className="w-6 h-6 text-robo-green" />
              <h2 className="text-2xl font-bold">All Participants</h2>
            </div>
            <button
              onClick={() => {
                setShowParticipants(!showParticipants);
                if (!showParticipants) fetchParticipants();
              }}
              className="px-4 py-2 bg-robo-green/20 hover:bg-robo-green/30 rounded-xl transition-colors"
            >
              {showParticipants ? 'Hide' : 'Show'} Participants
            </button>
          </div>

          {showParticipants && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="space-y-4"
            >
              {/* Stats */}
              <div className="grid grid-cols-4 gap-4 mb-6">
                <div className="bg-white/5 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-white">{stats.total}</p>
                  <p className="text-xs text-gray-400">Total</p>
                </div>
                <div className="bg-yellow-500/20 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-yellow-400">{stats.waiting}</p>
                  <p className="text-xs text-gray-400">Waiting</p>
                </div>
                <div className="bg-blue-500/20 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-blue-400">{stats.playing}</p>
                  <p className="text-xs text-gray-400">Playing</p>
                </div>
                <div className="bg-robo-green/20 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-robo-green">{stats.finished}</p>
                  <p className="text-xs text-gray-400">Finished</p>
                </div>
              </div>

              {/* Bulk Actions */}
              {selectedForDeletion.length > 0 && (
                <div className="bg-red-500/20 border-2 border-red-500/50 rounded-xl p-4 flex items-center justify-between">
                  <p className="text-sm">
                    <span className="font-bold">{selectedForDeletion.length}</span> participants selected for deletion
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={clearSelection}
                      className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors text-sm"
                    >
                      Clear
                    </button>
                    <button
                      onClick={handleBulkDelete}
                      className="px-4 py-2 bg-red-500 hover:bg-red-600 rounded-lg transition-colors text-sm font-bold"
                    >
                      Delete Selected
                    </button>
                  </div>
                </div>
              )}

              {/* Quick Select Buttons */}
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={selectAllFinished}
                  className="px-4 py-2 bg-yellow-500/20 hover:bg-yellow-500/30 rounded-lg transition-colors text-sm"
                >
                  Select All Finished
                </button>
                <button
                  onClick={clearSelection}
                  className="px-4 py-2 bg-white/5 hover:bg-white/10 rounded-lg transition-colors text-sm"
                >
                  Clear Selection
                </button>
                <button
                  onClick={fetchParticipants}
                  className="px-4 py-2 bg-white/5 hover:bg-white/10 rounded-lg transition-colors text-sm flex items-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  Refresh
                </button>
              </div>

              {/* Participants Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left border-b border-white/10">
                      <th className="pb-3 font-medium">Select</th>
                      <th className="pb-3 font-medium">Token</th>
                      <th className="pb-3 font-medium">Name</th>
                      <th className="pb-3 font-medium">Class</th>
                      <th className="pb-3 font-medium">Score</th>
                      <th className="pb-3 font-medium">Status</th>
                      <th className="pb-3 font-medium">Court</th>
                      <th className="pb-3 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {participants.map((participant) => (
                      <tr key={participant.id} className="hover:bg-white/5 transition-colors">
                        <td className="py-3">
                          <input
                            type="checkbox"
                            checked={selectedForDeletion.includes(participant.tokenNumber)}
                            onChange={() => toggleSelection(participant.tokenNumber)}
                            disabled={participant.status !== 'finished'}
                            className="rounded bg-white/10 border-white/30 disabled:opacity-30"
                          />
                        </td>
                        <td className="py-3 font-mono font-bold text-robo-green">
                          #{participant.tokenNumber}
                        </td>
                        <td className="py-3 font-medium">{participant.name}</td>
                        <td className="py-3 text-gray-400">{participant.className}</td>
                        <td className="py-3 font-bold">{participant.score}</td>
                        <td className="py-3">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            participant.status === 'finished' ? 'bg-robo-green/20 text-robo-green' :
                            participant.status === 'playing' ? 'bg-blue-500/20 text-blue-400' :
                            'bg-yellow-500/20 text-yellow-400'
                          }`}>
                            {participant.status}
                          </span>
                        </td>
                        <td className="py-3">
                          {participant.court ? (
                            <span className="px-2 py-1 rounded text-xs bg-purple-500/20 text-purple-400">
                              {participant.court}
                            </span>
                          ) : (
                            <span className="text-gray-500">-</span>
                          )}
                        </td>
                        <td className="py-3">
                          <button
                            onClick={() => handleDeleteParticipant(participant.id, participant.tokenNumber)}
                            disabled={participant.status === 'playing'}
                            className="px-3 py-1 bg-red-500/20 hover:bg-red-500/30 disabled:opacity-30 rounded-lg transition-colors text-xs text-red-400"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {participants.length === 0 && (
                  <p className="text-center text-gray-400 py-8">No participants yet</p>
                )}
              </div>

              <div className="mt-4 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl">
                <p className="text-xs text-yellow-400">
                  ⚠️ <strong>Warning:</strong> Deleted participants cannot be recovered. 
                  You can only delete participants with "finished" or "waiting" status.
                </p>
              </div>
            </motion.div>
          )}
        </GlowingCard>
      </div>

      {/* Instructions */}
      <div className="max-w-7xl mx-auto mt-6 p-4 bg-white/5 rounded-xl">
        <h3 className="font-bold mb-2">📋 How to Run a Match:</h3>
        <ol className="text-sm text-gray-400 space-y-1 list-decimal list-inside">
          <li>Click "Start Match" and enter first player's token number</li>
          <li>Click "Add Player 2" and enter second player's token number</li>
          <li>Use + and - buttons to update each player's score</li>
          <li>Click "Finish Match" when the game ends</li>
        </ol>
      </div>
    </div>
  );
}